'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Package, Cpu, Wrench, ArrowRight, Loader2, MapPin, CheckCircle2, AlertCircle, Tag } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

type ListingCategory = 'physical_goods' | 'digital_products' | 'services';

interface Listing {
  id: string;
  title: string;
  description: string;
  category: ListingCategory;
  price: number;
  currency: string;
  location: string;
  image_url: string | null;
  image_alt: string | null;
  tags: string[];
  created_at: string;
  user_profiles: { full_name: string; is_verified: boolean } | null;
}

const CATEGORY_CONFIG: Record<ListingCategory, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  physical_goods: { label: 'Physical', icon: <Package size={13} />, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  digital_products: { label: 'Digital', icon: <Cpu size={13} />, color: 'text-violet-600', bg: 'bg-violet-50 border-violet-200' },
  services: { label: 'Service', icon: <Wrench size={13} />, color: 'text-cyan-600', bg: 'bg-cyan-50 border-cyan-200' },
};

const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', ZAR: 'R', NGN: '₦', KES: 'KSh', GHS: '₵' };

function formatPrice(price: number, currency: string) {
  return `${CURRENCY_SYMBOLS[currency] || currency}${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function MarketplaceSection() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchListings() {
      const supabase = createClient();
      const { data } = await supabase
        .from('marketplace_listings')
        .select('id, title, description, category, price, currency, location, image_url, image_alt, tags, created_at, user_profiles(full_name, is_verified)')
        .eq('listing_status', 'active')
        .order('created_at', { ascending: false })
        .limit(6);
      if (data) setListings(data as Listing[]);
      setLoading(false);
    }
    fetchListings();
  }, []);

  if (!loading && listings.length === 0) return null;

  return (
    <section className="py-16 lg:py-20 bg-muted/30">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-1">Monezite Marketplace</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground">Buy &amp; Sell in the Community</h2>
            <p className="text-muted-foreground mt-1.5 text-sm max-w-lg">
              Physical goods, digital products, and services from verified members. Payments arranged directly between buyer and seller.
            </p>
          </div>
          <Link
            href="/marketplace"
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-primary border border-primary/30 rounded-xl hover:bg-primary/5 transition-colors shrink-0"
          >
            Browse All <ArrowRight size={14} />
          </Link>
        </div>

        {/* Off-platform notice */}
        <div className="flex items-center gap-2 p-3 bg-card border border-border rounded-xl mb-6 max-w-xl">
          <AlertCircle size={14} className="text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">No platform payments.</span> Buyers and sellers arrange payment directly off-site.
          </p>
        </div>

        {/* Listings */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={24} className="animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map((listing) => {
              const cat = CATEGORY_CONFIG[listing.category];
              const sellerName = listing.user_profiles?.full_name || 'Community Member';
              return (
                <div key={listing.id} className="bg-card border border-border rounded-2xl overflow-hidden card-shadow hover:card-shadow-md hover:border-primary/30 transition-all duration-200 flex flex-col group">
                  {listing.image_url ? (
                    <div className="h-40 overflow-hidden bg-muted">
                      <img
                        src={listing.image_url}
                        alt={listing.image_alt || listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className={`h-40 flex items-center justify-center ${cat.bg}`}>
                      <span className={`${cat.color} opacity-30`}>
                        {listing.category === 'physical_goods' ? <Package size={36} /> : listing.category === 'digital_products' ? <Cpu size={36} /> : <Wrench size={36} />}
                      </span>
                    </div>
                  )}

                  <div className="p-4 flex flex-col gap-2.5 flex-1">
                    <div className="flex items-center justify-between">
                      <span className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cat.bg} ${cat.color}`}>
                        {cat.icon}
                        {cat.label}
                      </span>
                      {listing.user_profiles?.is_verified && (
                        <span className="flex items-center gap-0.5 text-[10px] font-semibold text-success">
                          <CheckCircle2 size={10} />
                          Verified
                        </span>
                      )}
                    </div>

                    <h3 className="text-sm font-bold text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                      {listing.title}
                    </h3>

                    <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed flex-1">
                      {listing.description}
                    </p>

                    {listing.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {listing.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="flex items-center gap-0.5 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md">
                            <Tag size={8} />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <span className="text-base font-extrabold text-foreground">
                        {formatPrice(listing.price, listing.currency)}
                      </span>
                      {listing.location && (
                        <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <MapPin size={9} />
                          {listing.location}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                        <span className="text-[9px] font-bold text-primary">{sellerName.charAt(0)}</span>
                      </div>
                      <span className="text-xs text-muted-foreground truncate">{sellerName}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            href="/marketplace"
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 transition-all duration-150 active:scale-95"
          >
            Browse All Listings <ArrowRight size={15} />
          </Link>
          <Link
            href="/marketplace"
            className="flex items-center gap-2 px-6 py-3 border border-primary/30 text-primary text-sm font-semibold rounded-xl hover:bg-primary/5 transition-colors"
          >
            Post a Listing
          </Link>
        </div>
      </div>
    </section>
  );
}
